import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import StatCard from '../charts/StatCard';
import GaugeChart from '../charts/GaugeChart';
import { CurrencyDollarIcon, BanknotesIcon, ReceiptPercentIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';
import DrillDownModal from '../DrillDownModal';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

interface FinancialIndicatorsProps {
  dateRange: { start_date?: string; end_date?: string };
}

export default function FinancialIndicators({ dateRange }: FinancialIndicatorsProps) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState<{ metric: string; params?: Record<string, any> } | null>(null);

  const rangeParams: Record<string, any> = {
    ...(dateRange.start_date ? { date_debut: dateRange.start_date } : {}),
    ...(dateRange.end_date ? { date_fin: dateRange.end_date } : {}),
  };
  const openDrill = (metric: string, extra?: Record<string, any>) =>
    setDrill({ metric, params: { ...rangeParams, ...(extra || {}) } });

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);

      const queryString = params.toString();
      const url = queryString ? `/dashboard/analytics/financial/?${queryString}` : '/dashboard/analytics/financial/';
      const response = await api.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error loading financial indicators:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  if (!data) {
    return <div className="text-center text-gray-500">Aucune donnée disponible</div>;
  }

  // Combine revenue and collected data
  const combinedEvolution = data.revenue_evolution?.map((rev: any) => {
    const collected = data.collected_evolution?.find((c: any) => c.month === rev.month);
    return {
      month: new Date(rev.month).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
      revenue: rev.total,
      collected: collected?.total || 0
    };
  }) || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div onClick={() => openDrill('ca')} title="Voir le détail" className="cursor-pointer hover:-translate-y-0.5 transition-transform">
          <StatCard title="CA Total" value={`${data.total_revenue?.toLocaleString()} GNF`} icon={<CurrencyDollarIcon className="w-6 h-6" />} color="blue" />
        </div>
        <div onClick={() => openDrill('encaissements')} title="Voir le détail" className="cursor-pointer hover:-translate-y-0.5 transition-transform">
          <StatCard title="Recettes Encaissées" value={`${data.total_collected?.toLocaleString()} GNF`} icon={<BanknotesIcon className="w-6 h-6" />} color="green" />
        </div>
        <div onClick={() => openDrill('impayes')} title="Voir le détail" className="cursor-pointer hover:-translate-y-0.5 transition-transform">
          <StatCard title="Créances" value={`${data.outstanding_invoices?.toLocaleString()} GNF`} icon={<ReceiptPercentIcon className="w-6 h-6" />} color="yellow" />
        </div>
        <div onClick={() => openDrill('remises')} title="Voir le détail" className="cursor-pointer hover:-translate-y-0.5 transition-transform">
          <StatCard title="Remises" value={`${data.total_discounts?.toLocaleString()} GNF`} icon={<ReceiptPercentIcon className="w-6 h-6" />} color="red" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openDrill('ca')} title="Voir les factures de la période">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenus vs Encaissements <span className="text-xs font-normal text-gray-400">(cliquer → factures)</span></h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={combinedEvolution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => `${Number(value).toLocaleString()} GNF`} />
              <Legend />
              <Bar dataKey="revenue" fill="#3b82f6" name="Facturé" />
              <Line type="monotone" dataKey="collected" stroke="#10b981" strokeWidth={2} name="Encaissé" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Taux de Recouvrement</h3>
          <GaugeChart value={data.recovery_rate || 0} max={100} label="Taux de recouvrement global" color={data.recovery_rate >= 80 ? 'green' : data.recovery_rate >= 60 ? 'yellow' : 'red'} />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => openDrill('remises')} title="Voir les factures avec remise">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des Remises <span className="text-xs font-normal text-gray-400">(cliquer → détail)</span></h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data.discounts_evolution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { month: 'short' })} />
            <YAxis />
            <Tooltip labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR')} formatter={(value) => `${Number(value).toLocaleString()} GNF`} />
            <Line type="monotone" dataKey="total" stroke="#ef4444" strokeWidth={2} name="Remises" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Section Modes de Paiement */}
      {data.payment_methods && data.payment_methods.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Graphique en barres */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Montants par Mode de Paiement</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.payment_methods} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="payment_method" type="category" width={100} />
                <Tooltip formatter={(value) => `${Number(value).toLocaleString()} GNF`} />
                <Bar dataKey="total" fill="#3b82f6" name="Montant" cursor="pointer"
                  onClick={(d: any) => d && openDrill('encaissements', d.mode ? { mode: d.mode } : {})} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Graphique en camembert */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition des Paiements</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.payment_methods}
                  dataKey="total"
                  nameKey="payment_method"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  cursor="pointer"
                  onClick={(d: any) => d && openDrill('encaissements', d.mode ? { mode: d.mode } : {})}
                  label={({ payment_method, percent }) => `${payment_method}: ${(percent * 100).toFixed(0)}%`}
                >
                  {data.payment_methods.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${Number(value).toLocaleString()} GNF`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tableau détaillé des modes de paiement */}
      {data.payment_methods && data.payment_methods.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Détails des Modes de Paiement</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mode de Paiement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre de Paiements
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant Moyen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % du Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.payment_methods.map((method: any, index: number) => {
                  const totalPayments = data.payment_methods.reduce((sum: number, m: any) => sum + m.total, 0);
                  const percentage = (method.total / totalPayments) * 100;
                  const averageAmount = method.count > 0 ? method.total / method.count : 0;

                  return (
                    <tr key={index} className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openDrill('encaissements', method.mode ? { mode: method.mode } : {})}
                      title="Voir les paiements de ce mode">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="w-3 h-3 rounded-full mr-3"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="text-sm font-medium text-gray-900">{method.payment_method}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {method.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {method.total.toLocaleString()} GNF
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {averageAmount.toLocaleString()} GNF
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900">{percentage.toFixed(1)}%</span>
                          <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: COLORS[index % COLORS.length]
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    TOTAL
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    {data.payment_methods.reduce((sum: number, m: any) => sum + m.count, 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    {data.payment_methods.reduce((sum: number, m: any) => sum + m.total, 0).toLocaleString()} GNF
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    -
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    100%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {drill && (
        <DrillDownModal metric={drill.metric} params={drill.params} onClose={() => setDrill(null)} />
      )}
    </div>
  );
}
